import { LEGACY_PAGINATION_KEY_SET } from './constants';
import { getEntries, isKeyOf } from '@gov-nx/core/types';
import type {
	ActiveFilterComparable,
	HasFilterChangesParams,
} from './utils.types';
import isDeepEqual from 'fast-deep-equal';

const DEFAULT_PAGE_SIZE = 10;

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const mergeDeep = (
	target: Record<string, unknown>,
	source: Record<string, unknown>
): Record<string, unknown> => {
	const merged: Record<string, unknown> = { ...target };

	for (const [key, sourceValue] of Object.entries(source)) {
		const targetValue = merged[key];

		if (isRecord(targetValue) && isRecord(sourceValue)) {
			merged[key] = mergeDeep(targetValue, sourceValue);
			continue;
		}

		merged[key] = sourceValue;
	}

	return merged;
};

export const hasFilterChanges = <
	T extends object,
	C extends ActiveFilterComparable<T>,
>({
	currentFilter,
	initialState,
	ignoredKeys = [],
}: HasFilterChangesParams<T, C>) =>
	getEntries(currentFilter).some(([key, value]) => {
		if (!isKeyOf(initialState, key)) {
			return false;
		}

		if (ignoredKeys.includes(key)) {
			return false;
		}

		if (value === undefined || value === null) {
			return false;
		}

		if (typeof value === 'string' && value.trim().length === 0) {
			return false;
		}

		return !isDeepEqual(value, initialState[key]);
	});

export const isValidPageSize = (value: unknown): value is number =>
	typeof value === 'number' && Number.isFinite(value) && value > 0;

export const resolveLegacyPageSize = (
	filter: Record<string, unknown>,
	initialState: Record<string, unknown>
) => {
	if (isValidPageSize(filter.pageSize)) {
		return filter.pageSize;
	}

	if (isValidPageSize(filter.perPage)) {
		return filter.perPage;
	}

	if (isValidPageSize(initialState.pageSize)) {
		return initialState.pageSize;
	}

	return DEFAULT_PAGE_SIZE;
};

export const normalizePersistedFilterWithMigration = <T extends object>(
	key: string,
	initialState: T,
	filter: T
): {
	normalized: T;
	migrationPatch?: Record<string, unknown>;
} => {
	const initialAsObject = initialState as Record<string, unknown>;
	const filterAsObject = filter as Record<string, unknown>;

	const merged = mergeDeep(initialAsObject, filterAsObject) as T;

	if (!LEGACY_PAGINATION_KEY_SET.has(key)) {
		return { normalized: merged };
	}

	const mergedAsObject = merged as Record<string, unknown>;
	const migrationPatch: Record<string, unknown> = {};
	const shouldMigratePageSize = !isValidPageSize(filterAsObject.pageSize);

	if (shouldMigratePageSize) {
		const pageSize = resolveLegacyPageSize(filterAsObject, initialAsObject);
		mergedAsObject.pageSize = pageSize;
		migrationPatch.pageSize = pageSize;
	}

	if ('perPage' in mergedAsObject) {
		delete mergedAsObject.perPage;

		if (shouldMigratePageSize || filterAsObject.perPage !== undefined) {
			migrationPatch.perPage = undefined;
		}
	}

	return {
		normalized: merged,
		migrationPatch:
			Object.keys(migrationPatch).length > 0 ? migrationPatch : undefined,
	};
};
